import React, { useState, useEffect } from "react";
import Select from "react-select";
import BANK_DETAILS from "../../Utils/bankDetails";
import { userAPI } from "../../services/api";
import Toast from "../../components/Toast";
import useLeadStage from "../../hooks/useLeadStage";
import { LEAD_STAGE, leadStageToRouteMap } from "../../Utils/constants";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Building2 } from "lucide-react";
import FlowFooter from "../../components/FlowFooter";

const curr_page_lead_stage = [LEAD_STAGE.VERIFY_EMAIL_OTP];

export default function AccountAggregatorDigitapp() {
  const [selectedBank, setSelectedBank] = useState(null);
  const [phone, setPhone] = useState("");
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCustomBank, setShowCustomBank] = useState(false);
  const [customBankName, setCustomBankName] = useState("");
  const mobile_number = localStorage.getItem("userPhone") || "";
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });
  const [selectRef, setSelectRef] = useState(null);
  const { leadStage } = useLeadStage();
  const navigate = useNavigate();

  useEffect(() => {
    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(leadStageToRouteMap[leadStage]);
    }
  }, [leadStage, navigate]);

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 10) setPhone(value);
  };

  const handleInputFocus = () => {
    // Input focus handler
  };

  const handleInputBlur = () => {
    // Input blur handler
  };

  const showSuggestion =
    phone.length >= 3 &&
    mobile_number.startsWith(phone.slice(0, 3)) &&
    phone !== mobile_number;

  const handleSuggestionClick = () => {
    setPhone(mobile_number);
  };

  const handleBankChange = (option) => {
    setSelectedBank(option);
    if (option && option.value === "others") {
      setShowCustomBank(true);
      setCustomBankName("");
    } else {
      setShowCustomBank(false);
      setCustomBankName("");
    }
    if (selectRef) {
      selectRef.blur();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    hideToast();
    setTouched(true);
    if (!selectedBank) {
      showToast("Please select a bank");
      return;
    }
    if (selectedBank.value === "others" && !customBankName.trim()) {
      showToast("Please enter your bank name");
      return;
    }
    if (phone.length !== 10) {
      showToast("Please enter a valid 10-digit phone number");
      return;
    }
    try {
      setIsLoading(true);
      const response = await userAPI.generateBankingUrl({
        bankName:
          selectedBank.value === "others"
            ? customBankName.trim()
            : selectedBank.label,
        salaryAccountLinkedPhoneNo: phone,
        institutionId:
          selectedBank.value === "others"
            ? null
            : selectedBank.value.toString(),
      });

      if (response.data.redirectUrl) {
        window.location.href = response.data.redirectUrl;
      }
    } catch (error) {
      if (error.status === 500 || error.response?.status === 500) {
        navigate("/apply/upload-bank-statement");
        return;
      } else if (error.message.includes("External service error")) {
        navigate("/apply/upload-bank-statement");
        return;
      } else {
        showToast(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-2xl w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#1E3A8A]">
              Connect your <span className="text-[#FF6B35]">salary bank</span>
            </h1>
            <p className="text-sm sm:text-base text-[#64748B]">
              We use RBI-regulated Account Aggregator partners to fetch your statements securely.
            </p>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1">
                  Choose your salary bank
                </label>
                <Select
                  ref={setSelectRef}
                  options={[
                    ...BANK_DETAILS.map((bank) => ({
                      value: bank.id,
                      label: bank.name,
                    })),
                    { value: "others", label: "Others (Type your bank name)" },
                  ]}
                  value={selectedBank}
                  onChange={handleBankChange}
                  placeholder="Select Bank"
                  classNamePrefix="react-select"
                  className="text-left"
                  isSearchable={true}
                  isClearable={false}
                  noOptionsMessage={() => (
                    <div
                      className="px-3 py-2 text-[#1E293B] bg-[#F8FAFC] cursor-pointer hover:bg-[#F1F5F9] font-semibold"
                      onClick={() => {
                        handleBankChange({
                          value: "others",
                          label: "Others (Type your bank name)",
                        });
                        if (selectRef) selectRef.blur();
                      }}
                    >
                      Others (Type your bank name)
                    </div>
                  )}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      backgroundColor: "#FFFFFF",
                      borderColor: state.isFocused ? "#FF6B35" : "#E2E8F0",
                      boxShadow: state.isFocused ? "0 0 0 2px rgba(255,107,53,0.3)" : "none",
                      borderRadius: "0.75rem",
                      minHeight: "48px",
                    }),
                    singleValue: (base) => ({ ...base, color: "#1E293B" }),
                    input: (base) => ({ ...base, color: "#1E293B" }),
                    placeholder: (base) => ({ ...base, color: "#94A3B8" }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "#FFFFFF",
                      zIndex: 9999,
                      maxHeight: 300,
                      border: "1px solid #E2E8F0",
                      borderRadius: "0.75rem",
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected
                        ? "rgba(255,107,53,0.1)"
                        : state.isFocused
                        ? "#F8FAFC"
                        : "transparent",
                      color: state.isSelected ? "#FF6B35" : "#1E293B",
                    }),
                  }}
                />
                {touched && !selectedBank && (
                  <p className="text-xs text-red-500 mt-1">Please select a bank</p>
                )}

                {showCustomBank && (
                  <div className="mt-4">
                    <label className="block text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1">
                      Enter your salary bank name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={customBankName}
                        onChange={(e) => setCustomBankName(e.target.value)}
                        onFocus={() => handleInputFocus("customBank")}
                        onBlur={handleInputBlur}
                        placeholder="Bank name"
                        className={`w-full pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base transition-all ${
                          touched && selectedBank?.value === "others" && !customBankName.trim()
                            ? "border-red-500"
                            : "border-[#E2E8F0] focus:border-[#FF6B35]"
                        } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                      />
                      <div className="absolute left-0 top-0 bottom-0 w-12 bg-[#F8FAFC] rounded-l-xl sm:rounded-l-2xl flex items-center justify-center border-r border-[#E2E8F0]">
                        <Building2 size={18} className="text-[#FF6B35]" />
                      </div>
                    </div>
                    {touched && selectedBank?.value === "others" && !customBankName.trim() && (
                      <p className="text-xs text-red-500 mt-1">Please enter your bank name</p>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2 sm:py-3">
                <p className="text-xs sm:text-sm font-semibold text-amber-700 text-center">
                  Select the account in which salary is credited
                </p>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1">
                  Mobile number linked to salary account
                </label>
                <div className="relative">
                  <span className="absolute left-10 sm:left-10 top-1/2 -translate-y-12 text-[#1E293B] font-semibold text-sm sm:text-base z-10 pointer-events-none">+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={phone}
                    onChange={handlePhoneChange}
                    onFocus={() => handleInputFocus("phone")}
                    onBlur={handleInputBlur}
                    placeholder="10-digit mobile number"
                    className={`w-full pl-12 sm:pl-16 pr-3 sm:pr-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base transition-all ${
                      touched && phone.length !== 10
                        ? "border-red-500"
                        : "border-[#E2E8F0] focus:border-[#FF6B35]"
                    } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                  />
                </div>
                {showSuggestion && (
                  <button
                    type="button"
                    onClick={handleSuggestionClick}
                    className="mt-2 text-xs sm:text-sm text-[#FF6B35] hover:text-[#E55A2B] transition-colors font-semibold"
                  >
                    Use {mobile_number}
                  </button>
                )}
                {touched && phone.length !== 10 && (
                  <p className="text-xs text-red-500 mt-1">Please enter a valid 10-digit phone number</p>
                )}
              </div>

              <button
                type="submit"
                disabled={
                  isLoading ||
                  !selectedBank ||
                  phone.length !== 10 ||
                  (selectedBank?.value === "others" && !customBankName.trim())
                }
                className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <span className="h-4 w-4 sm:h-5 sm:w-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} className="text-white" />
                    Submit
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <FlowFooter />
    </div>
  );
}
